import { Injectable } from '@angular/core';
import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';
import type {
  SupplierBlend,
  SupplierBlendGroup,
  SupplierGroupTemplate,
} from '../models';
import type { SupplierAllocation } from '../models/supplier-allocation.model';
import {
  distributeCompletes,
  isAllocatableSupplier,
} from '../helpers/supplier-allocation/supplier-allocation.helper';

import type {
  SupplierManagementMemberRow,
  SupplierManagementRow,
} from '@ps/supplier-kernel';

// A blend allocation unit: either one blend group (groupName set, many members)
// or a single ungrouped supplier (groupName none, one member). The key is unique
// so quantities can be looked up per unit.
interface BlendUnit {
  readonly key: string;
  readonly groupName: Option<string>;
  readonly members: SupplierManagementRow[];
}

@Injectable({ providedIn: 'root' })
export class SupplierManagementService {
  /**
   * Zeroes every supplier's quantity, then spreads `goal` across the target
   * suppliers in card-dealing (round-robin) order — the first `goal % n` ids
   * get one extra unit. Non-target rows end up at 0. Returns a new list.
   */
  public distribute(
    suppliers: readonly SupplierManagementRow[],
    supplierIdsToDistribute: readonly string[],
    goal: number,
  ): SupplierManagementRow[] {
    const targetCount = supplierIdsToDistribute.length;
    if (targetCount === 0) {
      return suppliers.map((supplier) =>
        this._withCompletesGoal({ ...supplier, quantity: 0 }, goal),
      );
    }

    const quantityById = this._splitRoundRobin(goal, supplierIdsToDistribute);

    return suppliers.map((supplier) =>
      this._withCompletesGoal(
        {
          ...supplier,
          quantity: Option.from(quantityById.get(supplier.id)).unwrapOr(0),
        },
        goal,
      ),
    );
  }

  /**
   * State-shape (`SupplierAllocation`) analog of `distribute`: re-spreads the
   * survey goal evenly (round-robin) across the currently allocatable suppliers.
   * Groups count as one unit keyed by `groupId`, singles by their id. Inactive
   * rows drop to 0. Quantity and its two derived mirrors (`currentTarget`,
   * `completesGoal`) move together. Returns a new list.
   */
  public redistributeAllocationsEvenly(
    allocations: readonly SupplierAllocation[],
    goal: number,
  ): SupplierAllocation[] {
    const activeKeys = allocations
      .filter(isAllocatableSupplier)
      .map((allocation) => this._allocationKey(allocation));
    const quantityByKey = distributeCompletes(goal, activeKeys);

    return allocations.map((allocation) =>
      this._withAllocationQuantity(
        allocation,
        Option.from(
          quantityByKey.get(this._allocationKey(allocation)),
        ).unwrapOr(0),
      ),
    );
  }

  /**
   * Blend-driven analog of `redistributeAllocationsEvenly`: spreads the goal by
   * the blend's group allocation percentages rather than evenly. Each allocatable
   * unit takes `ceil(percentage * goal / 100)` of its matching blend group;
   * allocatable units the blend leaves uncovered share the remainder when the
   * blend's total allocation is below 100%. Inactive rows drop to 0. Quantity and
   * its two mirrors (`currentTarget`, `completesGoal`) move together (Path A —
   * `SupplierAllocation` carries `flexValue`, not the `flexibility` band field, so
   * `completesGoal` mirrors quantity exactly as the even method does). New list.
   */
  public redistributeAllocationsByBlend(
    allocations: readonly SupplierAllocation[],
    blend: SupplierBlend,
    goal: number,
  ): SupplierAllocation[] {
    const quantityByKey = this._blendAllocationQuantities(
      allocations,
      blend,
      goal,
    );

    return allocations.map((allocation) =>
      this._withAllocationQuantity(
        allocation,
        Option.from(
          quantityByKey.get(this._allocationKey(allocation)),
        ).unwrapOr(0),
      ),
    );
  }

  /**
   * State-shape (`SupplierAllocation`) analog of `applyGlobalPriceOverride`:
   * broadcasts one CPI across every allocation (global price override), groups
   * included. Returns a new list.
   */
  public applyGlobalPriceOverrideToAllocations(
    allocations: readonly SupplierAllocation[],
    cpi: number,
  ): SupplierAllocation[] {
    return allocations.map(
      (allocation): SupplierAllocation => ({ ...allocation, cpi }),
    );
  }

  /**
   * Re-enables a supplier that had no allocation: the whole goal is re-dealt
   * round-robin across every currently-allocated supplier plus this one, exactly
   * like the Distribute action. Non-targets stay at 0.
   */
  public checkSupplier(
    suppliers: readonly SupplierManagementRow[],
    supplierId: string,
    goal: number,
  ): SupplierManagementRow[] {
    const targetIds = suppliers
      .filter((supplier) => supplier.quantity > 0 || supplier.id === supplierId)
      .map((supplier) => supplier.id);
    return this.distribute(suppliers, targetIds, goal);
  }

  /**
   * Disables a supplier: its quantity is freed and dealt round-robin onto the
   * suppliers that already have allocation (their base is kept), and the disabled
   * supplier drops to 0. With no other allocated supplier the freed quantity has
   * nowhere to go — the UI prevents that case; here it just zeroes the row.
   */
  public uncheckSupplier(
    suppliers: readonly SupplierManagementRow[],
    supplierId: string,
    goal: number,
  ): SupplierManagementRow[] {
    const freed = Option.from(
      suppliers.find((supplier) => supplier.id === supplierId),
    ).mapOr(0, (supplier) => supplier.quantity);
    const recipientIds = suppliers
      .filter((supplier) => supplier.quantity > 0 && supplier.id !== supplierId)
      .map((supplier) => supplier.id);
    const incrementById = this._splitRoundRobin(freed, recipientIds);

    return suppliers.map((supplier) => {
      if (supplier.id === supplierId) {
        return this._withCompletesGoal({ ...supplier, quantity: 0 }, goal);
      }
      const increment = incrementById.get(supplier.id);
      return Option.from(increment).match({
        some: (extra) =>
          this._withCompletesGoal(
            { ...supplier, quantity: supplier.quantity + extra },
            goal,
          ),
        none: () => supplier,
      });
    });
  }

  /**
   * The selected rows expanded to deduped individual suppliers. Selected group
   * rows dissolve into their members (cpi inherited from the group, quantity 0
   * — via `_dissolve`); selected individuals pass through unchanged. Single
   * source of truth for both the grouping drawer's supplier list and
   * `applyGroup`'s member pool.
   */
  public flattenSelection(
    suppliers: readonly SupplierManagementRow[],
    selectedRowIds: readonly string[],
  ): SupplierManagementRow[] {
    const selected = new Set(selectedRowIds);
    const byId = new Map<string, SupplierManagementRow>();
    suppliers
      .filter((row) => selected.has(row.id))
      .flatMap((row) => (row.isGroup ? this._dissolve(row) : [row]))
      .forEach((row) => {
        if (!byId.has(row.id)) {
          byId.set(row.id, row);
        }
      });
    return [...byId.values()];
  }

  /**
   * Collapses the selected rows into one group row. Selected groups dissolve
   * and merge in; the group carries the drawer-supplied name/cpi, the sum of
   * the selected top-level rows' quantities, and 100% flexibility. Suppliers
   * dropped in the drawer (in the selection but not in `group.members`)
   * re-surface as ungrouped individual rows. The group is inserted at the
   * position of the first selected row. Same group-row shape as blend `_toRow`.
   */
  public applyGroup(
    suppliers: readonly SupplierManagementRow[],
    selectedRowIds: readonly string[],
    group: {
      groupName: string;
      cpi: number;
      members: readonly Omit<SupplierManagementMemberRow, 'feasibility'>[];
    },
    goal: number,
  ): SupplierManagementRow[] {
    const selected = new Set(selectedRowIds);
    const keptIds = new Set(group.members.map((member) => member.id));
    const flattened = this.flattenSelection(suppliers, selectedRowIds);
    const dropped = flattened.filter((row) => !keptIds.has(row.id));
    const quantity = suppliers
      .filter((row) => selected.has(row.id))
      .reduce((sum, row) => sum + row.quantity, 0);
    // The grouping drawer hands members back without feasibility, so it is
    // re-joined from the dissolved source rows the selection came from.
    const feasibilityById = new Map(
      flattened.map((row) => [row.id, row.feasibility]),
    );
    const members = group.members.map((member) => ({
      id: member.id,
      name: member.name,
      allocation: member.allocation,
      feasibility: feasibilityById.get(member.id) ?? Option.none,
    }));
    const groupRow: SupplierManagementRow = {
      id: group.groupName,
      name: group.groupName,
      isGroup: true,
      cpi: group.cpi,
      // A drawer-built group has no loaded allocation behind it: its own CPI is
      // the reset price, it takes work, and no PDS pricing applies.
      blockPrice: group.cpi,
      isActive: true,
      isSupplierActive: true,
      isPdsApplied: false,
      pdsFee: Option.none,
      quantity,
      flexibility: 100,
      completesGoal: this.flexibilityBounds(
        { quantity, flexibility: 100 },
        goal,
      ).max,
      feasibility: this._sumFeasibility(members),
      members,
    };
    const firstIndex = suppliers.findIndex((row) => selected.has(row.id));
    const remaining = suppliers.filter((row) => !selected.has(row.id));
    const insertAt = firstIndex < 0 ? remaining.length : firstIndex;
    return [
      ...remaining.slice(0, insertAt),
      groupRow,
      ...dropped,
      ...remaining.slice(insertAt),
    ];
  }

  /**
   * Applies a saved group template: the template's suppliers that are present
   * in the current list collapse into one group row carrying the template's
   * group-level allocation (`round(allocation% * goal)`); every other supplier
   * keeps its current quantity. Groups are dissolved first so a template can
   * regroup members that already sit in another group. Returns none when none
   * of the template's suppliers exist here — the caller surfaces that.
   */
  public applyGroupTemplate(
    suppliers: readonly SupplierManagementRow[],
    template: SupplierGroupTemplate,
    goal: number,
  ): Option<SupplierManagementRow[]> {
    const individuals = this._ungroup(suppliers);
    const allocationById = new Map(
      template.members.map((member) => [
        String(member.id),
        Option.from(member.allocation).unwrapOr(0),
      ]),
    );
    const matched = individuals.filter((row) => allocationById.has(row.id));
    if (matched.length === 0) {
      return Option.none;
    }

    const templateGroupCpi = this._averageCpi(matched);
    const groupRow: SupplierManagementRow = {
      id: template.groupName,
      name: template.groupName,
      isGroup: true,
      cpi: templateGroupCpi,
      blockPrice: templateGroupCpi,
      isActive: true,
      isSupplierActive: true,
      isPdsApplied: false,
      pdsFee: Option.none,
      quantity: this.quantityFromPercentage(
        Option.from(template.allocation).unwrapOr(0),
        goal,
      ),
      flexibility: 100,
      completesGoal: this.flexibilityBounds(
        {
          quantity: this.quantityFromPercentage(
            Option.from(template.allocation).unwrapOr(0),
            goal,
          ),
          flexibility: 100,
        },
        goal,
      ).max,
      feasibility: this._sumFeasibility(matched),
      members: matched.map((row) => ({
        id: row.id,
        name: row.name,
        allocation: Option.from(allocationById.get(row.id)).unwrapOr(0),
        feasibility: row.feasibility,
      })),
    };

    const matchedIds = new Set(matched.map((row) => row.id));
    const insertAt = individuals.findIndex((row) => matchedIds.has(row.id));
    const remaining = individuals.filter((row) => !matchedIds.has(row.id));
    return Option.some([
      ...remaining.slice(0, insertAt),
      groupRow,
      ...remaining.slice(insertAt),
    ]);
  }

  /** Whole-unit quantity for a percentage of the goal; never negative. */
  public quantityFromPercentage(percentage: number, goal: number): number {
    return Math.max(0, Math.round((percentage / 100) * goal));
  }

  /** Zeroes every supplier's quantity (and its completes goal); returns a new list. */
  public clear(
    suppliers: readonly SupplierManagementRow[],
  ): SupplierManagementRow[] {
    return suppliers.map((supplier) => ({
      ...supplier,
      quantity: 0,
      completesGoal: 0,
    }));
  }

  /** Broadcasts one flexibility value across every supplier; returns a new list. */
  public applyGlobalFlexibility(
    suppliers: readonly SupplierManagementRow[],
    flexibility: number,
    goal: number,
  ): SupplierManagementRow[] {
    return suppliers.map((supplier) =>
      this._withCompletesGoal({ ...supplier, flexibility }, goal),
    );
  }

  /** Broadcasts one CPI across every supplier (global price override); returns a new list. */
  public applyGlobalPriceOverride(
    suppliers: readonly SupplierManagementRow[],
    cpi: number,
  ): SupplierManagementRow[] {
    return suppliers.map((supplier) => ({ ...supplier, cpi }));
  }

  /**
   * The allocatable quantity band for a row given its flexibility and the goal.
   * 100% flexibility means the row can absorb the whole goal.
   */
  public flexibilityBounds(
    row: Pick<SupplierManagementRow, 'quantity' | 'flexibility'>,
    goal: number,
  ): { min: number; max: number } {
    if (row.quantity === goal) {
      return { min: row.quantity, max: row.quantity };
    }
    const spread = row.quantity * (row.flexibility / 100);
    const min = Math.ceil(Math.max(0, row.quantity - spread));
    const max =
      row.flexibility === 100
        ? goal
        : Math.floor(Math.min(goal, row.quantity + spread));
    return { min, max };
  }

  /**
   * Updates one row's quantity and re-derives its `completesGoal` (= MAX band);
   * every other row is untouched. The single entry point the panel's quantity
   * input uses so the goal-completion value never lags the edited quantity.
   */
  public changeQuantity(
    suppliers: readonly SupplierManagementRow[],
    rowId: string,
    quantity: number,
    goal: number,
  ): SupplierManagementRow[] {
    return suppliers.map((supplier) =>
      supplier.id === rowId
        ? this._withCompletesGoal({ ...supplier, quantity }, goal)
        : supplier,
    );
  }

  /** Percentage edit → whole quantity, then the same recompute as `changeQuantity`. */
  public changePercentage(
    suppliers: readonly SupplierManagementRow[],
    rowId: string,
    percentage: number,
    goal: number,
  ): SupplierManagementRow[] {
    return this.changeQuantity(
      suppliers,
      rowId,
      this.quantityFromPercentage(percentage, goal),
      goal,
    );
  }

  /**
   * Per-row (selective mode) flexibility edit; re-derives `completesGoal` since the
   * MAX band widens/narrows with flexibility.
   */
  public changeRowFlexibility(
    suppliers: readonly SupplierManagementRow[],
    rowId: string,
    flexibility: number,
    goal: number,
  ): SupplierManagementRow[] {
    return suppliers.map((supplier) =>
      supplier.id === rowId
        ? this._withCompletesGoal({ ...supplier, flexibility }, goal)
        : supplier,
    );
  }

  /**
   * Applies a saved blend across every supplier. Incoming panel groups are
   * dissolved to individuals, then re-grouped by the blend definition: each
   * blend group becomes one group row carrying the whole
   * `ceil(groupPercentage * goal / 100)`, ungrouped suppliers stay single rows.
   * Leftover completes are redistributed to eligible (unmatched) suppliers when
   * the blend's total allocation is below 100%. Flexibility follows the blend.
   */
  public applyBlend(
    suppliers: readonly SupplierManagementRow[],
    blend: SupplierBlend,
    goal: number,
  ): SupplierManagementRow[] {
    const units = this._blendUnits(this._ungroup(suppliers), blend);
    const quantityByUnit = this._unitQuantities(units, blend, goal);
    return units.map((unit) => this._toRow(unit, quantityByUnit, blend, goal));
  }

  /**
   * Dissolves the listed group rows into their member suppliers; every other row
   * is kept in place. Unlike the blend flow's `_dissolve`, the group keeps its
   * total work: its quantity is split evenly (round-robin) across the members and
   * each member's `completesGoal` is re-derived. cpi is inherited from the group.
   */
  public ungroup(
    suppliers: readonly SupplierManagementRow[],
    groupIds: readonly string[],
    goal: number,
  ): SupplierManagementRow[] {
    return suppliers.flatMap((row) =>
      row.isGroup && groupIds.includes(row.id)
        ? this._dissolveEvenly(row, goal)
        : [row],
    );
  }

  // The round-robin unit key for an allocation row: groups by name, singles by id.
  private _allocationKey(allocation: SupplierAllocation): string {
    return allocation.isGroup ? allocation.groupId : String(allocation.id);
  }

  // Assigns each allocatable unit its blend-group share of the goal, then spreads
  // any remainder across the units the blend doesn't cover when its total
  // allocation is under 100% (via the shared `_redistribute`).
  private _blendAllocationQuantities(
    allocations: readonly SupplierAllocation[],
    blend: SupplierBlend,
    goal: number,
  ): Map<string, number> {
    const quantityByKey = new Map<string, number>();
    const unmatchedKeys: string[] = [];
    let remaining = goal;
    let totalBlendAllocation = 0;

    allocations.filter(isAllocatableSupplier).forEach((allocation) => {
      const key = this._allocationKey(allocation);
      this._matchedBlendGroup(allocation, blend).match({
        some: (group) => {
          const percentage = Option.from(group.allocationPercentage).unwrapOr(
            0,
          );
          const quantity = Math.ceil((percentage * goal) / 100);
          quantityByKey.set(key, quantity);
          remaining -= quantity;
          totalBlendAllocation += percentage;
        },
        none: () => {
          quantityByKey.set(key, 0);
          unmatchedKeys.push(key);
        },
      });
    });

    const eligibleKeys = totalBlendAllocation < 100 ? unmatchedKeys : [];
    this._redistribute(eligibleKeys, remaining, quantityByKey);
    return quantityByKey;
  }

  // The blend group an allocation is allocated against — only groups carrying an
  // allocation percentage count. Group allocations match by name; single
  // allocations match an unnamed blend entry whose first member is this supplier.
  private _matchedBlendGroup(
    allocation: SupplierAllocation,
    blend: SupplierBlend,
  ): Option<SupplierBlendGroup> {
    return allocation.isGroup
      ? Option.from(
          blend.groups.find(
            (group) =>
              this._hasAllocation(group) &&
              group.groupName === allocation.groupName,
          ),
        )
      : Option.from(
          blend.groups.find(
            (group) =>
              this._hasAllocation(group) &&
              !group.groupName &&
              Option.from(group.members[0]).mapOr(
                false,
                (member) => member.id === allocation.id,
              ),
          ),
        );
  }

  // Sets an allocation's quantity and the two fields that mirror it, keeping the
  // discriminated-union branch intact.
  private _withAllocationQuantity(
    allocation: SupplierAllocation,
    quantity: number,
  ): SupplierAllocation {
    return {
      ...allocation,
      quantity,
      currentTarget: quantity,
      completesGoal: quantity,
    };
  }

  // Card-dealing split of `total` across `ids`: each gets floor(total/n), and the
  // first `total % n` ids (in list order) get one extra. Empty ids → empty map.
  private _splitRoundRobin(
    total: number,
    ids: readonly string[],
  ): Map<string, number> {
    const count = ids.length;
    if (count === 0) return new Map();

    const baseQuantity = Math.floor(total / count);
    const remainder = total % count;

    return new Map(
      ids.map((id, index) => [id, baseQuantity + (index < remainder ? 1 : 0)]),
    );
  }

  // Card-dealing (round-robin) split of the group's quantity across its dissolved
  // members: the first `quantity % n` get one extra unit so the parts sum to the
  // group total. Each row's completesGoal is re-derived from its new quantity.
  private _dissolveEvenly(
    row: SupplierManagementRow,
    goal: number,
  ): SupplierManagementRow[] {
    const members = this._dissolve(row);
    const memberCount = members.length;
    if (memberCount === 0) return members;

    const baseQuantity = Math.floor(row.quantity / memberCount);
    const remainder = row.quantity % memberCount;

    return members.map((member, index) =>
      this._withCompletesGoal(
        { ...member, quantity: baseQuantity + (index < remainder ? 1 : 0) },
        goal,
      ),
    );
  }

  private _withCompletesGoal(
    row: SupplierManagementRow,
    goal: number,
  ): SupplierManagementRow {
    return { ...row, completesGoal: this.flexibilityBounds(row, goal).max };
  }

  // One group row → its member suppliers as individual rows.
  private _dissolve(row: SupplierManagementRow): SupplierManagementRow[] {
    return row.members.map((member) => ({
      id: member.id,
      name: member.name,
      isGroup: false,
      cpi: row.cpi,
      // Pricing facts live on the group's allocation, so a dissolved member
      // inherits them the same way it inherits the group's CPI.
      blockPrice: row.blockPrice,
      isActive: row.isActive,
      isSupplierActive: row.isSupplierActive,
      isPdsApplied: row.isPdsApplied,
      pdsFee: row.pdsFee,
      quantity: 0,
      flexibility: 0,
      completesGoal: 0,
      feasibility: member.feasibility,
      members: [member],
    }));
  }

  // Dissolves every group row into its member suppliers; single rows pass
  // through. Deduped by id so a supplier shared across groups is counted once.
  private _ungroup(
    suppliers: readonly SupplierManagementRow[],
  ): SupplierManagementRow[] {
    const byId = new Map<string, SupplierManagementRow>();
    suppliers.forEach((row) => {
      const individuals = row.isGroup
        ? this._dissolve(row)
        : [{ ...row, isGroup: false }];
      individuals.forEach((individual) => {
        if (!byId.has(individual.id)) {
          byId.set(individual.id, individual);
        }
      });
    });
    return [...byId.values()];
  }

  // Groups individuals by their blend group; ungrouped ones become single units.
  private _blendUnits(
    individuals: readonly SupplierManagementRow[],
    blend: SupplierBlend,
  ): BlendUnit[] {
    const units: BlendUnit[] = [];
    const groupUnitByName = new Map<string, BlendUnit>();
    individuals.forEach((individual) => {
      this._resolveGroupName(individual, blend).match({
        some: (groupName) =>
          Option.from(groupUnitByName.get(groupName)).match({
            some: (unit) => unit.members.push(individual),
            none: () => {
              const unit: BlendUnit = {
                key: `group:${groupName}`,
                groupName: Option.some(groupName),
                members: [individual],
              };
              groupUnitByName.set(groupName, unit);
              units.push(unit);
            },
          }),
        none: () =>
          units.push({
            key: `single:${individual.id}`,
            groupName: Option.none,
            members: [individual],
          }),
      });
    });
    return units;
  }

  // The name of the blend group that lists this supplier, if any.
  private _resolveGroupName(
    individual: SupplierManagementRow,
    blend: SupplierBlend,
  ): Option<string> {
    const supplierId = Number(individual.id);
    return Option.from(
      blend.groups.find(
        (group) =>
          !!group.groupName &&
          group.members.some((member) => member.id === supplierId),
      ),
    ).map((group) => group.groupName);
  }

  // The blend group a unit is allocated against — only groups carrying an
  // allocation percentage count. Group units match by name; single units match
  // an unnamed blend entry whose first member is this supplier.
  private _matchedGroup(
    unit: BlendUnit,
    blend: SupplierBlend,
  ): Option<SupplierBlendGroup> {
    return unit.groupName.match({
      some: (groupName) =>
        Option.from(
          blend.groups.find(
            (group) =>
              this._hasAllocation(group) && group.groupName === groupName,
          ),
        ),
      none: () => {
        const supplierId = Number(unit.members[0].id);
        return Option.from(
          blend.groups.find(
            (group) =>
              this._hasAllocation(group) &&
              !group.groupName &&
              Option.from(group.members[0]).mapOr(
                false,
                (member) => member.id === supplierId,
              ),
          ),
        );
      },
    });
  }

  private _hasAllocation(group: SupplierBlendGroup): boolean {
    return Option.from(group.allocationPercentage).isSome();
  }

  // Assigns each unit its share of the goal, then spreads any remainder across
  // eligible unmatched units (each guaranteed at least one) when the blend's
  // total allocation leaves room.
  private _unitQuantities(
    units: readonly BlendUnit[],
    blend: SupplierBlend,
    goal: number,
  ): Map<string, number> {
    const quantityByUnit = new Map<string, number>();
    const unmatchedKeys: string[] = [];
    let remaining = goal;
    let totalBlendAllocation = 0;
    units.forEach((unit) => {
      this._matchedGroup(unit, blend).match({
        some: (group) => {
          const percentage = Option.from(group.allocationPercentage).unwrapOr(
            0,
          );
          const quantity = Math.ceil((percentage * goal) / 100);
          quantityByUnit.set(unit.key, quantity);
          remaining -= quantity;
          totalBlendAllocation += percentage;
        },
        none: () => {
          quantityByUnit.set(unit.key, 0);
          unmatchedKeys.push(unit.key);
        },
      });
    });
    const eligibleKeys = totalBlendAllocation < 100 ? unmatchedKeys : [];
    this._redistribute(eligibleKeys, remaining, quantityByUnit);
    return quantityByUnit;
  }

  private _redistribute(
    eligibleKeys: readonly string[],
    remaining: number,
    quantityByUnit: Map<string, number>,
  ): void {
    let pending = remaining;
    if (eligibleKeys.length > 0 && pending > 0) {
      while (pending > 0) {
        eligibleKeys.forEach((key) => {
          if (pending > 0) {
            quantityByUnit.set(
              key,
              Option.from(quantityByUnit.get(key)).unwrapOr(0) + 1,
            );
            pending -= 1;
          }
        });
      }
    }
    eligibleKeys.forEach((key) => {
      if (Option.from(quantityByUnit.get(key)).unwrapOr(0) === 0) {
        quantityByUnit.set(key, 1);
      }
    });
  }

  private _toRow(
    unit: BlendUnit,
    quantityByUnit: ReadonlyMap<string, number>,
    blend: SupplierBlend,
    goal: number,
  ): SupplierManagementRow {
    const quantity = Option.from(quantityByUnit.get(unit.key)).unwrapOr(0);
    const flexibility = this._blendFlexibility(unit, blend);
    const completesGoal = this.flexibilityBounds(
      { quantity, flexibility },
      goal,
    ).max;
    return unit.groupName.match({
      some: (groupName) => {
        const groupCpi = this._averageCpi(unit.members);
        return {
          id: groupName,
          name: groupName,
          isGroup: true,
          cpi: groupCpi,
          blockPrice: groupCpi,
          isActive: true,
          isSupplierActive: true,
          isPdsApplied: false,
          pdsFee: Option.none,
          quantity,
          flexibility,
          completesGoal,
          feasibility: this._sumFeasibility(unit.members),
          members: this._groupMemberRows(unit, blend),
        };
      },
      none: () => {
        const individual = unit.members[0];
        return {
          id: individual.id,
          name: individual.name,
          isGroup: false,
          cpi: individual.cpi,
          blockPrice: individual.blockPrice,
          isActive: individual.isActive,
          isSupplierActive: individual.isSupplierActive,
          isPdsApplied: individual.isPdsApplied,
          pdsFee: individual.pdsFee,
          quantity,
          flexibility,
          completesGoal,
          feasibility: individual.feasibility,
          members: individual.members,
        };
      },
    });
  }

  // Flexibility per blend mode: none pins 0, global broadcasts the blend value,
  // selective takes the matched group's value and defaults to 100 otherwise.
  private _blendFlexibility(unit: BlendUnit, blend: SupplierBlend): number {
    return match(blend.flexibilityMode)
      .with('none', () => 0)
      .with('global', () => blend.globalFlexibilityPercentage)
      .with('selective', () =>
        this._matchedGroup(unit, blend)
          .andThen((group) => Option.from(group.flexibilityPercentage))
          .unwrapOr(100),
      )
      .exhaustive();
  }

  private _averageCpi(members: readonly SupplierManagementRow[]): number {
    if (members.length === 0) return 0;
    const total = members.reduce((sum, member) => sum + member.cpi, 0);
    return total / members.length;
  }

  // A group's feasibility is the sum of whichever members carry one; `none`
  // when no member matched, so the row renders the empty value rather than 0.
  private _sumFeasibility(
    entries: readonly { feasibility: Option<number> }[],
  ): Option<number> {
    const matched = entries
      .map((entry) => entry.feasibility)
      .filter((feasibility) => feasibility.isSome());

    return matched.length
      ? Option.some(
          matched.reduce((sum, feasibility) => sum + feasibility.unwrap(), 0),
        )
      : Option.none;
  }

  // Member rows for a group, surfacing each member's blend allocation percentage
  // (shown in the row's expander); unknown members fall back to 0.
  private _groupMemberRows(
    unit: BlendUnit,
    blend: SupplierBlend,
  ): SupplierManagementMemberRow[] {
    const group = unit.groupName.andThen((groupName) =>
      Option.from(blend.groups.find((entry) => entry.groupName === groupName)),
    );
    return unit.members.map((individual) => ({
      id: individual.id,
      name: individual.name,
      allocation: group
        .andThen((matched) =>
          Option.from(
            matched.members.find(
              (member) => member.id === Number(individual.id),
            ),
          ),
        )
        .andThen((member) => Option.from(member.allocation))
        .unwrapOr(0),
      feasibility: individual.feasibility,
    }));
  }
}
